import PermissionButton, { currentCan } from '../../permissions/PermissionButton';
import SalesBlockedWarning from '../shared/SalesBlockedWarning';
import React, { useState, useEffect, useContext, useRef } from "react";
import { Form, Input, Button, Table, Popconfirm, Space } from "antd";
import { DeleteOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const EditableContext = React.createContext(null);

// Create a context for keyboard navigation with active row tracking
const KeyboardNavContext = React.createContext({
  activeCell: null,
  setActiveCell: () => {},
  navigateToCell: () => {},
  activeRowKey: null,
  setActiveRowKey: () => {}
});

const EditableRow = ({ index, isModified, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

// Simple orange dot indicator
const OrangeDot = () => (
  <span 
    className="orange-dot"
    style={{ 
      display: 'inline-block',
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: '#ff8c00',
      marginRight: '6px',
    }}
  />
);

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  isModified,
  rowIndex,
  colIndex,
  navigateToCell,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  const { setActiveRowKey } = useContext(KeyboardNavContext);
  
  // For debugging - create a reference to store the cell position info
  const cellInfoRef = useRef({
    rowIndex,
    colIndex,
    dataIndex,
    recordId: record?.CODE_ART || null
  });

  // Update the ref when props change - with null check
  useEffect(() => {
    if (record) {
      cellInfoRef.current = {
        rowIndex,
        colIndex,
        dataIndex,
        recordId: record.CODE_ART || null
      };
    }
  }, [rowIndex, colIndex, dataIndex, record]);

  useEffect(() => {
    if (editing && record) {
      inputRef.current?.focus();
      // Add auto-selection when entering edit mode
      inputRef.current?.select();
      
      // Set the active row when editing begins
      if (record.CODE_ART) {
        setActiveRowKey(record.CODE_ART);
      }
      
      // Debug
      console.log('Cell entered edit mode:', cellInfoRef.current);
    }
  }, [editing, record, setActiveRowKey]);

  const toggleEdit = () => {
    if (!editable || !record) return; // Prevent editing if not allowed or record is undefined
    setEditing(!editing);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async () => {
    if (!record) return; // Bail early if record is undefined
    
    try {
      const values = await form.validateFields();
      toggleEdit();
      console.log('Saving cell:', cellInfoRef.current, 'with values:', values);
      if (dataIndex === 'PV_TTC') {
        handleSave({ ...record, ...values, REMISE: 0 });
      } else {
        handleSave({ ...record, ...values });
      }
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  const cancel = () => {
    if (!record) return; // Bail early if record is undefined
    
    setEditing(false);
    console.log('Canceled editing for cell:', cellInfoRef.current);
    form.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    console.log('Key pressed in cell:', e.key);
    
    // Explicitly handle all arrow keys to override default browser and Ant Design behavior
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        save();
        break;
      case 'Escape':
        e.preventDefault();
        cancel();
        break;
      case 'ArrowUp':
        e.preventDefault();
        save();
        setTimeout(() => navigateToCell(rowIndex - 1, colIndex), 50);
        break;
      case 'ArrowDown':
        e.preventDefault();
        save();
        setTimeout(() => navigateToCell(rowIndex + 1, colIndex), 50);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        save();
        setTimeout(() => navigateToCell(rowIndex, colIndex - 1), 50);
        break;
      case 'ArrowRight':
        e.preventDefault();
        save();
        setTimeout(() => navigateToCell(rowIndex, colIndex + 1), 50);
        break;
      default:
        break;
    }
  };

  // Format and validate input for number fields
  const handleInputChange = (e) => {
    const { value } = e.target;
    
    // Only allow numbers and decimal points
    if (/^-?\d*\.?\d*$/.test(value) || value === '') {
      form.setFieldsValue({ [dataIndex]: value });
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <div className="compact-edit-container">
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `${title} is required.`,
            },
            {
              validator: (_, value) => {
                if (isNaN(value) || value === '') {
                  return Promise.reject(`${title} must be a number`);
                }
                
                // Additional validation for specific fields
                if (dataIndex === 'QTE' && value <= 0) {
                  return Promise.reject('Quantity must be greater than 0');
                }
                if (dataIndex === 'REMISE' && (value < 0 || value > 100)) {
                  return Promise.reject('Discount must be between 0 and 100');
                }
                if (dataIndex === 'PV_TTC' && value <= 0) {
                  return Promise.reject('Price must be greater than 0');
                }
                
                return Promise.resolve();
              }
            }
          ]}
        >
          {/* Replace InputNumber with regular Input for full control */}
          <Input
            ref={inputRef}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            className="compact-input"
            size="small"
            style={{ width: '100%' }}
            autoComplete="off"
          />
        </Form.Item>
        
        <div className="edit-actions">
          <div className="compact-buttons">
            <PermissionButton permissionModule="invoices" permissionAction="update"
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              onClick={save}
              className="compact-button"
            />
            <PermissionButton permissionModule="invoices" permissionAction="update"
              danger
              size="small"
              icon={<CloseOutlined />}
              onClick={cancel}
              className="compact-button"
            />
          </div>
        </div>
      </div>
    ) : (
      <div 
        className="editable-cell-value-wrap" 
        style={{ 
          paddingRight: 12,
          color: isModified ? '#f5222d' : 'inherit',
          cursor: editable ? 'pointer' : 'not-allowed'
        }} 
        onClick={toggleEdit}
        data-row-index={rowIndex}
        data-col-index={colIndex}
        data-cell-id={record ? `${record.CODE_ART}-${dataIndex}` : ''}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

const NestedFactureTable = ({ items, handleSave, handleDelete, isValidated, isManager }) => {
  const [modifiedRows, setModifiedRows] = useState(new Set());
  const [modifiedFields, setModifiedFields] = useState(new Map());
  const [originalData, setOriginalData] = useState(new Map());
  const [sortedInfo, setSortedInfo] = useState({
    field: 'CODE_ART',
    order: 'ascend'
  });
  
  // State to track the currently editable cell position
  const [activeCell, setActiveCell] = useState(null);
  
  // Add state to track the active row
  const [activeRowKey, setActiveRowKey] = useState(null);
  
  // Refs for table cells to enable direct access
  const cellRefs = useRef({});
  
  useEffect(() => {
    const newOriginalData = new Map();
    if (Array.isArray(items)) { // Add check for items array
      items.forEach(item => {
        if (item && item.CODE_ART) { // Add null check for item
          newOriginalData.set(item.CODE_ART, { ...item });
        }
      });
    }
    setOriginalData(newOriginalData);
  }, [items]);

  const handleTableChange = (pagination, filters, sorter) => {
    // Ensure we're setting field and order properly
    const updatedSortInfo = {
      field: sorter.field || 'CODE_ART',
      order: sorter.order || 'ascend'
    };
    setSortedInfo(updatedSortInfo);
  };

  const handleModifiedSave = (row) => {
    if (!row || !row.CODE_ART) return; // Add null check
    
    // Convert string values to numbers for numeric fields
    const processedRow = { ...row };
    if ('QTE' in processedRow) {
      processedRow.QTE = Number(processedRow.QTE);
    }
    if ('PV_TTC' in processedRow) {
      processedRow.PV_TTC = Number(processedRow.PV_TTC);
    }
    if ('REMISE' in processedRow) {
      processedRow.REMISE = Number(processedRow.REMISE);
    }
    
    const original = originalData.get(processedRow.CODE_ART);
    const modifiedFieldsForRow = new Set();
    
    if (original) {
      Object.keys(processedRow).forEach(key => {
        if (processedRow[key] !== original[key]) {
          modifiedFieldsForRow.add(key);
        }
      });
    }

    if (modifiedFieldsForRow.size > 0) {
      setModifiedRows(prev => new Set(prev).add(processedRow.CODE_ART));
      setModifiedFields(prev => new Map(prev).set(processedRow.CODE_ART, modifiedFieldsForRow));
    }

    handleSave(processedRow);
  };

  // Get all editable column keys
  const getEditableColumnKeys = () => {
    return columns
      .filter(col => col.editable)
      .map(col => col.dataIndex);
  };

  // Function to navigate between cells
  const navigateToCell = (rowIndex, colIndex) => {
    console.log('Navigate to cell called with:', rowIndex, colIndex);
    const sortedItems = getSortedItems();
    const editableColumns = getEditableColumnKeys();
    
    // Ensure we have items
    if (!Array.isArray(sortedItems) || sortedItems.length === 0) {
      console.log('No items available');
      return;
    }
    
    // Ensure the column is editable
    if (colIndex < 0 || colIndex >= editableColumns.length) {
      console.log('Column index out of bounds');
      return;
    }
    
    // Ensure the row exists
    if (rowIndex < 0 || rowIndex >= sortedItems.length) {
      console.log('Row index out of bounds');
      return;
    }
    
    const targetRow = sortedItems[rowIndex];
    if (!targetRow || !targetRow.CODE_ART) {
      console.log('Target row is invalid');
      return;
    }
    
    const targetColumn = editableColumns[colIndex];
    
    console.log('Target found:', targetRow.CODE_ART, targetColumn);
    
    // Set the active cell
    setActiveCell({ rowKey: targetRow.CODE_ART, columnKey: targetColumn });
    
    // Set active row as well
    setActiveRowKey(targetRow.CODE_ART);
    
    // Try to find and click the cell to enter edit mode
    console.log('Looking for cell ref:', `${targetRow.CODE_ART}-${targetColumn}`);
    
    // Add a small delay to ensure the component has updated
    setTimeout(() => {
      const cellElement = cellRefs.current[`${targetRow.CODE_ART}-${targetColumn}`];
      console.log('Cell refs:', cellRefs.current);
      if (cellElement) {
        console.log('Cell element found, clicking...');
        cellElement.click();
      } else {
        console.log('Cell element not found!');
        
        // Find the element directly in the DOM as a fallback
        const cellSelector = `[data-cell-id="${targetRow.CODE_ART}-${targetColumn}"]`;
        const domCell = document.querySelector(cellSelector);
        
        if (domCell) {
          console.log('Found cell in DOM, clicking...');
          domCell.click();
        } else {
          console.log('Cell not found in DOM either', cellSelector);
        }
      }
    }, 100);
  };

  const getSortedItems = () => {
    if (!Array.isArray(items)) return []; // Add null check
    
    // Default to CODE_ART ascending if no sort order is set
    const field = sortedInfo.field || 'CODE_ART';
    const order = sortedInfo.order || 'ascend';
    
    return [...items].sort((a, b) => {
      if (!a || !b || !a.CODE_ART || !b.CODE_ART) return 0; // Add null check
      
      if (field === 'CODE_ART') {
        if (order === 'ascend') {
          return a.CODE_ART.localeCompare(b.CODE_ART);
        } else {
          return b.CODE_ART.localeCompare(a.CODE_ART);
        }
      }
      return 0;
    });
  };

  const columns = [
    {
      title: "Code Art",
      dataIndex: "CODE_ART",
      key: "CODE_ART",
      sorter: true,
      sortOrder: sortedInfo.field === 'CODE_ART' && sortedInfo.order,
      width: 120,
      render: (text) => (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center',
          minWidth: '100px',
        }}>
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>{text}</span>
        </div>
      )
    },
    {
      title: "Article",
      dataIndex: "ARTICLE",
      key: "ARTICLE",
      render: (text, record) => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.85em' }}><span>{text}</span><SalesBlockedWarning item={record} /></span>,
    },
    {
      title: "Stock",
      dataIndex: ["article", "stocks"],
      key: "qte_stk",
      render: (stocks) => {
        if (!stocks || stocks.length === 0) return 0;
        return stocks[0]?.qte_stk || 0;
      },
    },
    
    
    {
      title: "Qté",
      dataIndex: "QTE",
      key: "QTE",
      width: 80,
      
    },
    {
      title: "Prix(ttc)",
      dataIndex: "PV_TTC",
      key: "PV_TTC",
      width: 80,
      
      render: (text) => ` ${parseFloat(text).toFixed(2)}`,
    },
    {
      title: "%R",
      dataIndex: "REMISE",
      key: "REMISE",
      width: 80,
      
      render: (text) => `${parseFloat(text).toFixed(2)}%`,
    },
    {
      title: "TVA",
      dataIndex: "TVA",
      key: "TVA",
      width: 50,
      render: (text) => `${parseFloat(text).toFixed(2)}%`,
    },
    {
      title: "Montant",
      dataIndex: "TotalTTC",
      key: "TotalTTC",
      render: (_, record) => {
        if (!record) return '0.00'; // Add null check
        return ` ${(record.QTE * record.PV_TTC * (1 - record.REMISE / 100)).toFixed(2)}`;
      },
    },
    {
      title: "",
      key: "actions",
      render: (_, record) => {
        if (!record) return null; // Add null check
        
        // Check if the user is a manager and the item is not validated
        // Use the isManager prop with a default value of false if not provided
        const canDelete = currentCan('invoices', 'update') && (!isValidated && (isManager ?? false));
        
        return canDelete && (
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cet article?"
            onConfirm={() => handleDelete(record.CODE_ART)}
            okText="Oui"
            cancelText="Non"
          >
            <PermissionButton permissionModule="invoices" permissionAction="update" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        );
      },
    }
  ];

  const expandedColumns = [
    {
      title: "UAV",
      dataIndex: "uav",
      key: "uav",
      render: (text) => text || 'N/A',
    },
    {
      title: "Qty per Carton",
      dataIndex: "qt_cart",
      key: "qt_cart",
      render: (text) => text || 'N/A',
    },
    {
      title: "Qty per Package",
      dataIndex: "q_paquet",
      key: "q_paquet",
      render: (text) => text || 'N/A',
    },
    {
      title: "REF OEM",
      dataIndex: "REF_OEM",
      key: "REF_OEM",
      render: (text) => text || 'N/A',
    },
  ];

  const expandedRowRender = (record) => {
    if (!record) return null; // Add null check
    
    const data = [{
      key: record.CODE_ART,
      uav: record.article?.uav,
      qt_cart: record.article?.qt_cart,
      q_paquet: record.article?.q_paquet,
      REF_OEM: record.article?.REF_OEM,
    }];

    return (
      <Table
        columns={expandedColumns}
        dataSource={data}
        pagination={false}
        showHeader={true}
        style={{ margin: 0 }}
        size="small"
      />
    );
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const editableColumns = columns.map((col, colIdx) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record, rowIndex) => {
        if (!record) return {}; // Add null check
        
        // Get the actual column index among editable columns
        const editableColIndex = getEditableColumnKeys().indexOf(col.dataIndex);
        
        return {
          record,
          editable: col.editable && !isValidated,
          dataIndex: col.dataIndex,
          title: col.title,
          handleSave: handleModifiedSave,
          isModified: modifiedFields.get(record.CODE_ART)?.has(col.dataIndex),
          rowIndex: rowIndex,
          colIndex: editableColIndex,
          navigateToCell: navigateToCell,
          ref: node => {
            // Store a reference to the cell div for direct access
            if (node && record.CODE_ART) {
              cellRefs.current[`${record.CODE_ART}-${col.dataIndex}`] = node;
            }
          }
        };
      },
    };
  });

  // Get a row class name based on its state (modified and/or active)
  const getRowClassName = (record) => {
    if (!record || !record.CODE_ART) return ''; // Add null check
    
    const classes = [];
    
    if (modifiedRows.has(record.CODE_ART)) {
      classes.push('modified-row');
    }
    
    if (activeRowKey === record.CODE_ART) {
      classes.push('active-row');
    }
    
    return classes.join(' ');
  };

  return (
    <div className="nested-facture-table">
      <style>
        {`
          .nested-facture-table .modified-row {
            background-color: #e6f7ff !important;
          }
          .nested-facture-table .modified-row:hover > td {
            background-color: #bae7ff !important;
          }
          .nested-facture-table .editable-cell-value-wrap.modified {
            color: #f5222d;
          }
          .orange-dot {
            box-shadow: 0 0 3px rgba(255, 140, 0, 0.5);
          }
          .ant-table-cell {
            padding: 4px 6px !important;
            line-height: 1.1 !important;
          }
          .editable-cell-value-wrap {
            padding: 0 2px;
            line-height: 1.1;
          }
          .compact-edit-container {
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: 2px;
            padding: 0;
          }
          .compact-input {
            font-size: 14px !important;
            padding: 0 2px !important;
            height: 25px !important;
          }
          .edit-actions {
            display: flex;
            gap: 6px;
            justify-content: space-between;
            width: 100%;
          }
          .compact-buttons {
            display: flex;
            gap: 2px;
            margin-top: 2px;
          }
          .compact-button {
            height: 18px !important;
            width: 18px !important;
            padding: 0 !important;
            font-size: 10px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          
          /* Active row styling with horizontal black lines only */
          .nested-facture-table .active-row td {
            outline: none !important;
            border-top: 2px solid black !important;
            border-bottom: 2px solid black !important;
          }
          
          /* Fix for first and last cells to maintain consistent look */
          .nested-facture-table .active-row td:first-child {
            border-left: none !important;
          }
          
          .nested-facture-table .active-row td:last-child {
            border-right: none !important;
          }
          
          .ant-form-item-margin-offset {
            margin-bottom: 0 !important;
          }
          .ant-input {
            padding: 0 2px !important;
          }
        `}
      </style>
    
      <KeyboardNavContext.Provider value={{ 
        activeCell, 
        setActiveCell, 
        navigateToCell,
        activeRowKey,
        setActiveRowKey
      }}>
        <Table
          components={components}
          rowClassName={getRowClassName}
          bordered
          dataSource={getSortedItems()}
          columns={editableColumns}
          rowKey="CODE_ART"
          expandable={{
            expandedRowRender,
            expandRowByClick: false,
          }}
          pagination={false}
          scroll={{ x: true, y: 500 }}
          size="small"
          sticky={{ offsetHeader: 0 }}
          onChange={handleTableChange}
          onRow={(record, rowIndex) => {
            if (!record) return {}; // Add null check
            return {
              'data-row-index': rowIndex,
            };
          }}
        />
      </KeyboardNavContext.Provider>
      
      {/* Display help text for keyboard shortcuts */}
      <div style={{ marginTop: '8px', fontSize: '12px', color: '#888' }}>
        <p>Fleche navigation: Utilisez les fleches pour la navigation</p>
      </div>
    </div>
  );
};

export default NestedFactureTable;
